// src/utils/canauxNotification.ts — SCE Issues
// Canaux de notification (Lot 10 bis) : lecture, écriture, règles. Pur.
//
// Chacun choisit comment SCE Issues le prévient : Mail, Teams, les deux, ou
// l'appli seulement. Un seul réglage par personne, valable pour toutes les
// notifications (décision du 25/09). Il est rangé dans la colonne
// CanauxNotification de SA ligne de SCE_UserPrefs, sous une forme que Power
// Automate lit sans rien décoder : `Mail,Teams`, `Mail`, `Teams` ou `Appli`.
//
// ─── La cloche (03/11) ───
//
// L'appli est un canal TOUJOURS là : la cloche de l'en-tête reçoit tout
// (SCE_Notifications). Mail et Teams décochés = « l'appli seulement », écrit
// `Appli` : ni Mail ni Teams dedans, donc aucun flow n'envoie rien — pas même
// le flow 6, dont la règle « `Aucun` = le mail quand même » ne vaut que pour
// l'ancienne valeur. `Aucun`, écrit avant la cloche, se lit comme `Appli`.
//
// ─── Pourquoi une valeur plutôt qu'une cellule vide ───
//
// Une cellule VIDE veut dire « jamais réglé » → les deux canaux, comme avant
// le Lot 10 bis. SharePoint ne distingue pas une chaîne vide d'une absence de
// valeur : « l'appli seulement » doit donc s'écrire explicitement.
//
// Côté flow : `contains(canaux, 'Mail')` / `contains(canaux, 'Teams')`, et une
// valeur absente vaut les deux.

import { isCoordinateur } from '@/config/coordinateurs.config'

export type Canal = 'Mail' | 'Teams'

/** Ordre d'affichage ET d'écriture. */
export const CANAUX: readonly Canal[] = ['Mail', 'Teams']

/** Ancienne valeur de « rien » (avant la cloche) : lue comme `APPLI_SEULE`. */
export const AUCUN_CANAL = 'Aucun'

/** « L'appli seulement » : ni mail ni Teams, la cloche reçoit tout (03/11). */
export const APPLI_SEULE = 'Appli'

/** La personne ne veut ni mail ni Teams (`Appli`, ou l'ancien `Aucun`) ? */
export function appliSeulement(brut: string | null | undefined): boolean {
  const t = (brut ?? '').trim().toLowerCase()
  return t === APPLI_SEULE.toLowerCase() || t === AUCUN_CANAL.toLowerCase()
}

/**
 * Valeur de la colonne → canaux choisis. Vide ou illisible → les deux (le
 * comportement d'avant : personne ne perd une alerte sur une faute de frappe).
 */
export function lireCanaux(brut: string | null | undefined): Canal[] {
  const texte = (brut ?? '').trim()
  if (texte === '') return [...CANAUX]
  if (appliSeulement(texte)) return []
  const jetons = texte.split(/[,;]/).map((j) => j.trim().toLowerCase())
  const choisis = CANAUX.filter((c) => jetons.includes(c.toLowerCase()))
  return choisis.length > 0 ? choisis : [...CANAUX]
}

/** Canaux choisis → valeur de la colonne. */
export function ecrireCanaux(canaux: readonly Canal[]): string {
  const choisis = CANAUX.filter((c) => canaux.includes(c))
  return choisis.length > 0 ? choisis.join(',') : APPLI_SEULE
}

/**
 * Un coordinateur (ou un suppléant) garde le mail ou Teams : les nouveaux
 * problèmes de ses métiers doivent le joindre même appli fermée. Les autres
 * peuvent passer à l'appli seulement (décision du 25/09, cloche du 03/11).
 */
export function canalObligatoire(upn: string): boolean {
  return isCoordinateur(upn)
}

/**
 * Les canaux proposés : un compte invité (externe) n'a que le mail — Teams
 * n'écrit pas à un compte invité, le flow 6 ne lui envoie que le mail (02/11).
 */
export function canauxProposes(invite: boolean): readonly Canal[] {
  return invite ? ['Mail'] : CANAUX
}

/** Les canaux choisis, réduits à ceux qu'on propose : ce que voit et bascule la page. */
export function canauxVisibles(canaux: readonly Canal[], invite: boolean): Canal[] {
  const proposes = canauxProposes(invite)
  return canaux.filter((c) => proposes.includes(c))
}

/** Basculer un canal : le nouveau choix, ou null s'il est interdit. */
export function basculerCanal(
  actuels: readonly Canal[],
  canal: Canal,
  obligatoire: boolean,
): Canal[] | null {
  const suivants = actuels.includes(canal)
    ? actuels.filter((c) => c !== canal)
    : CANAUX.filter((c) => c === canal || actuels.includes(c))
  if (obligatoire && suivants.length === 0) return null
  return suivants
}
